import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle } from '../../../../components/common';
import { useOrdenLiquidacionStore } from '../store/useOrdenLiquidacionStore';
import type { OrdenLiquidacion, OrdenLiquidacionDetalle, EstadoType } from '../types';
import { estadoLabels, estadoColors } from '../types';

const OrdenLiquidacionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [selectedOrden] = useState<OrdenLiquidacion | null>(null);

  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const {
    ordenes,
    loading,
    error,
    fetchOrdenes,
    updateEstado,
    deleteOrden,
    clearError,
  } = useOrdenLiquidacionStore();

  useEffect(() => {
    fetchOrdenes();
  }, [fetchOrdenes]);

  const filteredOrdenes = ordenes.filter((orden) => {
    switch (selectedTab) {
      case 0: return orden.estado === 'PEN';
      case 1: return orden.estado === 'APR';
      case 2: return orden.estado === 'ANU';
      case 3: return true;
      default: return true;
    }
  });

  const countByEstado = (estado: EstadoType) =>
    ordenes.filter((o) => o.estado === estado).length;

  const handleAprobar = async (id: number) => {
    await updateEstado(id, 'APR');
  };

  const handleAnular = async (id: number) => {
    await updateEstado(id, 'ANU');
  };

  const handleDelete = async (id: number) => {
    await deleteOrden(id);
  };

  const agruparPorPresentacion = (detalles: OrdenLiquidacionDetalle[]) => {
    const grouped = new Map<string, OrdenLiquidacionDetalle[]>();
    for (const d of detalles) {
      const key = d.presentacion?.descripcion || `Presentación ${d.idPresentacion}`;
      const existing = grouped.get(key) || [];
      existing.push(d);
      grouped.set(key, existing);
    }
    return grouped;
  };

  const columns: GridColDef[] = [
    { field: 'numero', headerName: '#', width: 80 },
    {
      field: 'fechaProceso',
      headerName: 'Fecha',
      width: 100,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-ES') : '',
    },
    {
      field: 'documento',
      headerName: 'N° DERC',
      width: 130,
      valueGetter: (_value: unknown, row: OrdenLiquidacion) =>
        row.documento?.numeroDocumento || '',
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 130,
      valueGetter: (_value: unknown, row: OrdenLiquidacion) =>
        row.documento?.asociacion?.descripcion || '',
    },
    {
      field: 'duenio',
      headerName: 'Dueño',
      width: 130,
      valueGetter: (_value: unknown, row: OrdenLiquidacion) =>
        (row as OrdenLiquidacion & { duenio?: string }).duenio || '',
    },
    {
      field: 'subLoteCantidad',
      headerName: 'Sub Lote',
      width: 80,
      type: 'number',
      valueGetter: (_value: unknown, row: OrdenLiquidacion) =>
        (row as OrdenLiquidacion & { subLoteCantidad?: number }).subLoteCantidad || '',
    },
    {
      field: 'proveedor',
      headerName: 'Proveedor',
      width: 150,
      valueGetter: (_value: unknown, row: OrdenLiquidacion) =>
        row.proveedor?.razonSocial || '',
    },
    { field: 'tipoProceso', headerName: 'Proceso', width: 90 },
    { field: 'tipoOperacion', headerName: 'Operación', width: 110 },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 70,
      type: 'number',
    },
    {
      field: 'totalKilos',
      headerName: 'Total Kg',
      width: 100,
      type: 'number',
      valueFormatter: (value: number) => value?.toFixed(2),
    },
    {
      field: 'subTotal',
      headerName: 'Sub Total',
      width: 110,
      type: 'number',
      valueFormatter: (value: number) => value ? `S/. ${value.toFixed(2)}` : '',
    },
    {
      field: 'totalLiquidar',
      headerName: 'Total Liquidar',
      width: 120,
      type: 'number',
      valueFormatter: (value: number) => value ? `S/. ${value.toFixed(2)}` : '',
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={estadoLabels[params.value as EstadoType] || params.value}
          color={estadoColors[params.value as EstadoType] || 'default'}
          size="small"
          sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalle">
            <IconButton
              size="small"
              onClick={() => navigate(`/administracion/orden-liquidacion/nueva?guiaId=${params.row.idGuiaProduccion}&tipoProceso=${params.row.tipoProceso}&tipoOperacion=${params.row.tipoOperacion}`)}
              sx={{ color: '#64748b', '&:hover': { color: '#0f2847', bgcolor: '#f0f7ff' } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.estado === 'PEN' && (
            <>
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/administracion/orden-liquidacion/nueva?guiaId=${params.row.idGuiaProduccion}&tipoProceso=${params.row.tipoProceso}&tipoOperacion=${params.row.tipoOperacion}`)}
                  sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Aprobar">
                <IconButton
                  size="small"
                  onClick={() => handleAprobar(params.row.id)}
                  sx={{ color: '#10b981', '&:hover': { bgcolor: '#ecfdf5' } }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Anular">
                <IconButton
                  size="small"
                  onClick={() => handleAnular(params.row.id)}
                  sx={{ color: '#f59e0b', '&:hover': { bgcolor: '#fffbeb' } }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(params.row.id)}
                  sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  if (loading && ordenes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#3b82f6' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <PageTitle
          title="Ordenes de Liquidacion"
          subtitle="Gestion de ordenes de liquidacion de materia prima"
        />
        {/* Generar Orden se hace desde Guías de Producción */}
      </Box>

      {successMsg && (
        <Alert
          severity="success"
          onClose={() => setSuccessMsg(null)}
          sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}
        >
          {successMsg}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          onClose={clearError}
          sx={{ mb: 2, borderRadius: 2, fontFamily: '"DM Sans", sans-serif' }}
        >
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_e, newValue) => setSelectedTab(newValue)}
          sx={{
            px: 2,
            bgcolor: '#fff',
            '& .MuiTab-root': {
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              minHeight: 48,
              color: '#64748b',
              '&.Mui-selected': { color: '#0f2847' },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #3b82f6, #10b981)',
            },
          }}
        >
          <Tab label={`Pendientes (${countByEstado('PEN')})`} />
          <Tab label={`Aprobadas (${countByEstado('APR')})`} />
          <Tab label={`Anuladas (${countByEstado('ANU')})`} />
          <Tab label={`Lista (${ordenes.length})`} />
        </Tabs>
      </Paper>

      <DataGrid
        rows={filteredOrdenes}
        columns={columns}
        loading={loading}
        getRowId={(row: OrdenLiquidacion) => row.id}
        pageSize={25}
      />

      {/* Dialog detalle */}
      <Dialog
        open={detalleDialogOpen}
        onClose={() => setDetalleDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#0f2847' }}>
          Orden de Liquidacion {selectedOrden?.numero}
        </DialogTitle>
        <DialogContent>
          {selectedOrden && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                {[
                  ['Fecha', new Date(selectedOrden.fechaProceso).toLocaleDateString('es-ES')],
                  ['DERC', selectedOrden.documento?.numeroDocumento],
                  ['Proveedor', selectedOrden.proveedor?.razonSocial],
                  ['Mallas', selectedOrden.numeroMallas],
                  ['Proceso', selectedOrden.tipoProceso],
                  ['Operacion', selectedOrden.tipoOperacion],
                ].map(([label, value]) => (
                  <Typography key={String(label)} sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' }}>
                    <Box component="span" sx={{ color: '#64748b', fontWeight: 500 }}>{label}:</Box>{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>{String(value)}</Box>
                  </Typography>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {Array.from(agruparPorPresentacion(selectedOrden.detalles)).map(
                ([presentacion, detalles]) => {
                  const subTotalKilos = detalles.reduce((sum, d) => sum + d.kilos, 0);
                  const subTotalMonto = detalles.reduce((sum, d) => sum + d.monto, 0);

                  return (
                    <Box key={presentacion} sx={{ mb: 2 }}>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#0f2847', mb: 1 }}>
                        {presentacion}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              {['Calibre', 'Codigo', 'Kilos', 'Precio', 'Monto'].map((h) => (
                                <TableCell
                                  key={h}
                                  align={h === 'Calibre' ? 'left' : 'right'}
                                  sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detalles.map((d) => (
                              <TableRow key={d.id} sx={{ '&:hover': { bgcolor: '#f0f7ff' } }}>
                                <TableCell sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' }}>{d.calibre?.descripcion || d.idCalibre}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: '#64748b' }}>{d.codigoPrecio}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' }}>{d.kilos.toFixed(1)}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem' }}>S/. {d.precioUnitario.toFixed(2)}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600 }}>S/. {d.monto.toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              <TableCell colSpan={2} sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>Subtotal</TableCell>
                              <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>{subTotalKilos.toFixed(1)}</TableCell>
                              <TableCell />
                              <TableCell align="right" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.8rem' }}>S/. {subTotalMonto.toFixed(2)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                }
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem' }}>
                  <Box component="span" sx={{ color: '#64748b' }}>KG Soles:</Box>{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>S/. {selectedOrden.subTotal.toFixed(2)}</Box>
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem', color: '#ef4444' }}>
                  Detraccion 1.50%: - S/. {selectedOrden.detraccion.toFixed(2)}
                </Typography>
                <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.9rem', color: '#ef4444' }}>
                  Servicio: - S/. {selectedOrden.totalServicio.toFixed(2)}
                </Typography>
                <Divider sx={{ width: 250, my: 0.5 }} />
                <Typography sx={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', fontWeight: 700, color: '#0f2847' }}>
                  Total: S/. {selectedOrden.totalLiquidar.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDetalleDialogOpen(false)} sx={{ fontFamily: '"DM Sans", sans-serif', textTransform: 'none' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenLiquidacionPage;
